// frontend/admin-portal/src/components/procurement/AwardQuotationModal.jsx
import React, { useEffect, useState } from "react";
import { Modal, Button, Input, Alert, Badge } from "@fiberhome/shared-ui";
import { purchaseOrderService } from "../../services/purchaseOrderService";

export default function AwardQuotationModal({
  open,
  supplierData,
  onClose,
  onAwarded,
}) {
  const [form, setForm] = useState({
    expectedAt: "",
    taxRate: 0,
    shipping: 0,
    notes: "",
    shippingAddress: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setForm({
      expectedAt: "",
      taxRate: 0,
      shipping: 0,
      notes: "",
      shippingAddress: "",
    });
    setError("");
    setResult(null);
  }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const quotation = supplierData?.quotation;
  const lines = supplierData?.lines || [];
  const available = lines.filter((l) => l.status === "available");

  const subtotal = available.reduce(
    (s, l) => s + (Number(l.lineTotal) || 0),
    0,
  );
  const taxAmount = (subtotal * Number(form.taxRate || 0)) / 100;
  const total = subtotal + taxAmount + Number(form.shipping || 0);

  const handleAward = async () => {
    setSaving(true);
    setError("");
    try {
      const po = await purchaseOrderService.awardQuotation({
        quotationId: quotation.id,
        expectedAt: form.expectedAt || null,
        taxRate: Number(form.taxRate) || 0,
        shipping: Number(form.shipping) || 0,
        notes: form.notes || null,
        shippingAddress: form.shippingAddress || null,
      });
      setResult(po);
      onAwarded?.(po);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Award failed");
    } finally {
      setSaving(false);
    }
  };

  const currency = quotation?.currency || "IQD";

  return (
    <Modal
      open={open}
      onClose={saving ? undefined : onClose}
      title={
        result
          ? `PO ${result.code} Created`
          : `Award to ${supplierData?.supplier?.name || ""}`
      }
      size="lg"
      footer={
        result ? (
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleAward} loading={saving}>
              Create Purchase Order
            </Button>
          </>
        )
      }
    >
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      {result ? (
        <>
          <Alert variant="success" className="mb-3">
            Purchase Order <strong>{result.code}</strong> was created in draft
            status. You can now edit it and send it to the supplier.
          </Alert>
        </>
      ) : (
        <>
          <Alert variant="info" className="mb-3">
            This will create a <strong>draft</strong> Purchase Order for{" "}
            <strong>{supplierData?.supplier?.name}</strong> from quotation{" "}
            <Badge variant="primary">{quotation?.code}</Badge>. Only available
            items will be included ({available.length} of {lines.length}).
          </Alert>

          <div className="row g-3 mb-3">
            <div className="col-md-4">
              <Input
                label="Expected delivery"
                type="date"
                value={form.expectedAt}
                onChange={(e) => set("expectedAt", e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <Input
                label="Tax Rate (%)"
                type="number"
                step="0.1"
                value={form.taxRate}
                onChange={(e) => set("taxRate", e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <Input
                label="Shipping"
                type="number"
                value={form.shipping}
                onChange={(e) => set("shipping", e.target.value)}
              />
            </div>
            <div className="col-12">
              <Input
                label="Shipping Address"
                value={form.shippingAddress}
                onChange={(e) => set("shippingAddress", e.target.value)}
              />
            </div>
            <div className="col-12">
              <Input
                label="Notes"
                value={form.notes}
                onChange={(e) => set("notes", e.target.value)}
              />
            </div>
          </div>

          <div className="fh-po-totals-preview">
            <div className="fh-po-totals-preview__row">
              <span>Subtotal ({available.length} items)</span>
              <span>
                {currency} {subtotal.toLocaleString()}
              </span>
            </div>
            <div className="fh-po-totals-preview__row">
              <span>Tax</span>
              <span>
                {currency} {taxAmount.toLocaleString()}
              </span>
            </div>
            <div className="fh-po-totals-preview__row">
              <span>Shipping</span>
              <span>
                {currency} {Number(form.shipping).toLocaleString()}
              </span>
            </div>
            <div className="fh-po-totals-preview__row is-total">
              <strong>Total</strong>
              <strong>
                {currency} {total.toLocaleString()}
              </strong>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
}
